import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchVehicleBestValueDashboard,
  recalculateAllBestValue,
  recalculateListingBestValue,
} from "../../../../services/vehicleBestValueApi";
import { vehicleMarketDetailPath } from "../../../../constants/transport";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const LABEL_OPTIONS = [
  { value: "", label: "All labels" },
  { value: "EXCELLENT_VALUE", label: "Excellent Value" },
  { value: "GOOD_VALUE", label: "Good Value" },
  { value: "FAIR_VALUE", label: "Fair Value" },
  { value: "LOW_VALUE", label: "Low Value" },
  { value: "RISKY", label: "Risky" },
  { value: "INSUFFICIENT_DATA", label: "Insufficient Data" },
];

const VehicleBestValuePage = ({ onClose }) => {
  const [overview, setOverview] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [labelFilter, setLabelFilter] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleBestValueDashboard({
        bestValueLabel: labelFilter || undefined,
        limit: 200,
      });
      setOverview(data.overview || null);
      setListings(data.listings || []);
    } catch (err) {
      setErrorMessage(err.message || "Unable to load best value dashboard.");
    } finally {
      setLoading(false);
    }
  }, [labelFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalcAll = async () => {
    try {
      const result = await recalculateAllBestValue({ limit: 300 });
      setToast(`Recalculated ${result.result?.processed ?? 0} listings.`);
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  const handleRecalcRow = async (listingId) => {
    try {
      await recalculateListingBestValue(listingId);
      setToast("Score recalculated.");
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-best-value-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>Vehicle Best Value</h1>
          <p className="muted">
            Explainable 0–100 best-value scores for active listings (Mobility / v1).
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="crm-btn crm-btn-secondary" onClick={handleRecalcAll}>
            Recalculate batch
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {errorMessage ? <AdminStatePanel variant="error" message={errorMessage} /> : null}

      {overview ? (
        <div className="admin-summary-cards">
          <div className="admin-summary-card">
            <span>Excellent Value</span>
            <strong>{overview.excellentValue}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Good Value</span>
            <strong>{overview.goodValue}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Fair Value</span>
            <strong>{overview.fairValue}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Low Value</span>
            <strong>{overview.lowValue}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Risky</span>
            <strong>{overview.risky}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Insufficient Data</span>
            <strong>{overview.insufficientData}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Average Score</span>
            <strong>{overview.averageScore ?? "—"}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Missing Score</span>
            <strong>{overview.listingsMissingScore}</strong>
          </div>
        </div>
      ) : null}

      <div className="admin-dashboard__toolbar">
        <label>
          Label filter
          <select value={labelFilter} onChange={(e) => setLabelFilter(e.target.value)}>
            {LABEL_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <AdminTableContainer loading={loading} empty={!listings.length && !loading}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Source</th>
              <th>Price</th>
              <th>Fair Value</th>
              <th>Score</th>
              <th>Label</th>
              <th>Mileage</th>
              <th>Year</th>
              <th>Quality</th>
              <th>Source Health</th>
              <th>Canonical</th>
              <th>Calculated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((row) => (
              <tr key={row.vehicleListingId}>
                <td>{row.title}</td>
                <td>{row.sourceName}</td>
                <td>{formatCop(row.currentPriceCop)}</td>
                <td>{row.fairValueLabel || "—"}</td>
                <td>{row.bestValueScore ?? "—"}</td>
                <td>{row.bestValueLabel || "—"}</td>
                <td>{row.mileageKm != null ? `${row.mileageKm} km` : "—"}</td>
                <td>{row.year ?? "—"}</td>
                <td>{row.dataQualityStatus || "—"}</td>
                <td>{row.sourceHealthStatus || "—"}</td>
                <td>{row.canonicalStatus || "—"}</td>
                <td>{formatDate(row.calculatedAt)}</td>
                <td className="admin-table__actions">
                  <Link to={vehicleMarketDetailPath(row.vehicleListingId)}>Detail</Link>
                  <button
                    type="button"
                    className="crm-btn crm-btn-secondary crm-btn--compact"
                    onClick={() => handleRecalcRow(row.vehicleListingId)}
                  >
                    Recalculate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableContainer>
    </div>
  );
};

export default VehicleBestValuePage;
